import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import {
  disabled,
  form,
  FormField,
  FormRoot,
  maxLength,
  required,
  validate,
} from '@angular/forms/signals';
import { ActivatedRoute, Router } from '@angular/router';
import { CardButtonsComponent } from 'common/card/buttons/card-buttons.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatCheckbox } from '@angular/material/checkbox';
import { DialogComponent } from 'common/dialog/dialog.component';
import {
  DialogFooterDirective,
  DialogHeaderTitleDirective,
} from 'common/dialog/dialog-slots.directive';
import { requiredAndNotWhitespaceOnlySignal } from 'common/form/form.service';
import { MatFormField } from '@angular/material/form-field';
import { MatError, MatInput, MatLabel } from '@angular/material/input';
import { ApiManufacturer, ApiProduct, ApiProductRevision, ApiV1Service, ViewService } from 'api';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProgressSpinnerComponent } from 'common/progress-spinner/progress-spinner.component';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  BILLING_ACKNOWLEDGEMENT_REQUIRED_CODE,
  BillingStatusService,
} from 'common/billing/billing-status.service';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';

export type UniqueNessType = 'PRODUCT' | 'REVISION';

export interface AddProductDialogData {
  manufacturer: ApiManufacturer;
  origin: 'dashboard' | 'products';
}

interface AddProductFormModel {
  name: string;
  description: string;
  createRevision: boolean;
  revisionName: string;
  intakeType: string;
  openRevision: boolean;
}

@Component({
  templateUrl: 'add-product.component.html',
  styleUrl: 'add-product.component.scss',
  imports: [
    TranslateModule,
    FormsModule,
    FormField,
    FormRoot,
    MatFormField,
    MatLabel,
    MatInput,
    CardButtonsComponent,
    MatCheckbox,
    DialogComponent,
    DialogHeaderTitleDirective,
    DialogFooterDirective,
    MatError,
    MatProgressSpinnerModule,
    ProgressSpinnerComponent,
    MatSelect,
    MatOption,
    AutofocusDirective,
    ButtonDirective,
  ],
})
export class AddProductComponent implements OnInit {
  /**
   * Gates opening the add-product dialog behind the billing acknowledgement
   * prompt (CP-151 §10.4 situation 1): if approval is required, only the
   * confirm dialog is shown first, and the add-product dialog is opened
   * afterwards only once the user has successfully acknowledged. Returns
   * `null` when acknowledgement was required but declined/cancelled.
   */
  public static async open(
    dialog: MatDialog,
    billingStatusService: BillingStatusService,
    data: AddProductDialogData,
  ): Promise<MatDialogRef<AddProductComponent> | null> {
    const canProceed = await billingStatusService.ensureAcknowledgementForCreation();
    if (!canProceed) {
      return null;
    }
    return dialog.open(AddProductComponent, { data });
  }

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiV1Service);
  private readonly viewService = inject(ViewService);
  private readonly billingStatusService = inject(BillingStatusService);
  private readonly dialogRef = inject(MatDialogRef<AddProductComponent>);
  protected readonly showRevisionForm = computed(() => this.productFormModel().createRevision);
  protected errorMessageKey = signal<string | undefined>(undefined);
  protected readonly uniquenessErrors = signal<Partial<Record<'name' | 'revisionName', boolean>>>(
    {},
  );

  readonly data = inject<AddProductDialogData>(MAT_DIALOG_DATA);

  private isDialog = false;

  readonly productFormModel = signal<AddProductFormModel>({
    name: '',
    description: '',
    createRevision: false,
    revisionName: '0.1',
    intakeType: '',
    openRevision: true,
  });

  readonly productForm = form(
    this.productFormModel,
    (schemaPath) => {
      requiredAndNotWhitespaceOnlySignal(schemaPath.name);
      required(schemaPath.revisionName);
      maxLength(schemaPath.name, 255);
      maxLength(schemaPath.revisionName, 255);
      validate(schemaPath.name, () =>
        this.uniquenessErrors().name ? { kind: 'uniqueness' } : null,
      );
      validate(schemaPath.revisionName, () =>
        this.uniquenessErrors().revisionName ? { kind: 'uniqueness' } : null,
      );
      disabled(schemaPath.revisionName, { when: () => !this.productFormModel().createRevision });
      disabled(schemaPath.intakeType, { when: () => !this.productFormModel().createRevision });
      disabled(schemaPath.openRevision, { when: () => !this.productFormModel().createRevision });
    },
    {
      submission: {
        action: async (field) => this.onSubmit(field().value()),
      },
    },
  );
  supportedIntakeTypes = toSignal(
    this.apiService.getIntakeTypes().pipe(
      map(
        (intakeTypes) => new Map(intakeTypes.map((intakeType) => [intakeType.ident, intakeType])),
      ),
      catchError(() => {
        this.errorMessageKey.set('intake.typesLoading.error');
        return of(undefined);
      }),
    ),
    {
      initialValue: undefined,
    },
  );
  submitting = signal(false);

  constructor() {
    effect(() => {
      const supportedIntakeTypes = this.supportedIntakeTypes();
      if (supportedIntakeTypes) {
        const firstIntakeType = supportedIntakeTypes.keys().next().value;
        if (firstIntakeType) {
          this.productFormModel.update((model) => ({ ...model, intakeType: firstIntakeType }));
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.billingStatusService.refresh();
    this.viewService.triggerRequirementsFetch().subscribe();
    if (this.dialogRef) {
      this.isDialog = true;
    }
  }

  protected async onCancel() {
    if (this.isDialog) {
      this.dialogRef.close();
    } else {
      await this.router.navigate(['..'], { relativeTo: this.route });
    }
  }

  async onSubmit(model: AddProductFormModel) {
    this.submitting.set(true);
    try {
      const newProductAndRevision = await this.saveProductAndRevision(model);
      const newProduct = newProductAndRevision.product;
      const newRevision = newProductAndRevision.revision;
      await this.billingStatusService.refresh();

      await this.finishSubmission(
        newProduct,
        newRevision,
        model.createRevision && model.openRevision,
      );
    } catch (error) {
      if (await this.retryAfterAcknowledgement(error)) {
        try {
          const newProductAndRevision = await this.saveProductAndRevision(model);
          await this.billingStatusService.refresh();
          await this.finishSubmission(
            newProductAndRevision.product,
            newProductAndRevision.revision,
            model.createRevision && model.openRevision,
          );
        } catch (retryError) {
          console.log(retryError);
          this.submitting.set(false);
        }
        return;
      }
      console.log(error);
      this.submitting.set(false);
    }
  }

  private async finishSubmission(
    newProduct: ApiProduct,
    newRevision: ApiProductRevision | undefined,
    openRevision: boolean,
  ) {
    if (this.isDialog) {
      const productRoute = ['products', newProduct.id, newRevision?.id];

      if (openRevision) {
        this.dialogRef.close();
        await this.router.navigate(productRoute);
        return;
      }

      this.dialogRef.close(newProduct);
      if (this.data.origin === 'dashboard') {
        await this.router.navigate(['products']);
      }
    } else {
      await this.router.navigate(['..', newProduct.id], { relativeTo: this.route });
    }
  }

  /**
   * Handles a `403 BILLING_ACKNOWLEDGEMENT_REQUIRED` rejection of the create-product call
   * (CP-151 §9.3/§10.4 situation 1): shows the non-dismissible approval prompt and reports
   * whether the caller may retry the creation once.
   */
  private async retryAfterAcknowledgement(error: unknown): Promise<boolean> {
    if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
      return false;
    }
    if (BillingStatusService.getBillingErrorCode(error) !== BILLING_ACKNOWLEDGEMENT_REQUIRED_CODE) {
      return false;
    }
    return await this.billingStatusService.handleCreationAcknowledgementRequired(error);
  }

  private async saveProductAndRevision(model: AddProductFormModel): Promise<{
    product: ApiProduct;
    revision?: ApiProductRevision;
  }> {
    const manufacturer = this.data.manufacturer;
    const newProduct = await firstValueFrom(
      this.apiService.createProduct(manufacturer.id!, {
        name: model.name,
        description: model.description,
      }),
    );
    let newRevision: ApiProductRevision | undefined = undefined;
    if (model.createRevision) {
      newRevision = await firstValueFrom(
        this.apiService.createProductRevision(newProduct.id, {
          revision: model.revisionName,
          intakeTypeIdent: model.intakeType,
        }),
      );
    }
    return { product: newProduct, revision: newRevision };
  }

  protected async validateUniqueness(type: UniqueNessType) {
    const fieldName = type === 'PRODUCT' ? 'name' : 'revisionName';
    const value = this.productFormModel()[fieldName];
    if (!value.trim()) {
      return;
    }
    if (type === 'PRODUCT') {
      await this.checkUniqueness('name', value, () => this.viewService.validateProductName(value));
    }
    // Revision-name validation is product-scoped, but the product does not exist yet.
  }

  protected clearUniquenessError(field: 'name' | 'revisionName') {
    this.uniquenessErrors.update((errors) => ({ ...errors, [field]: false }));
  }

  private async checkUniqueness(
    field: 'name' | 'revisionName',
    value: string,
    validator: () => import('rxjs').Observable<unknown>,
  ) {
    try {
      await firstValueFrom(validator());
      if (this.productFormModel()[field] === value) {
        this.clearUniquenessError(field);
      }
    } catch {
      if (this.productFormModel()[field] === value) {
        this.uniquenessErrors.update((errors) => ({ ...errors, [field]: true }));
      }
    }
  }
}
